import {
  Title,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Paper,
  Progress,
  Badge,
  List,
  ThemeIcon,
  Divider,
  SimpleGrid,
  Checkbox,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import {
  IconUpload,
  IconFile,
  IconX,
  IconCircleCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconCategory,
} from '@tabler/icons-react';
import { useState } from 'react';
import { adminService } from '../../services/admin';

interface ProductImportResult {
  deleted: number;
  imported: number;
  skipped: number;
  errorCount: number;
  errors: string[];
  message: string;
}

interface CategoryImportResult {
  parentsCreated: number;
  subcategoriesCreated: number;
  skipped: number;
  errorCount: number;
  errors: string[];
  message: string;
}

// ── Category Import ───────────────────────────────────────────────────────────

function CategoryImportSection() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CategoryImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await adminService.importCategoriesTxt(file);
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Import failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap="md">
      <Group gap="xs">
        <ThemeIcon variant="light" color="violet" size="md">
          <IconCategory size={16} />
        </ThemeIcon>
        <Text fw={600} size="lg">Import Categories</Text>
      </Group>

      <Alert icon={<IconInfoCircle size={16} />} color="violet" variant="light">
        Upload <Text span ff="monospace" size="sm">bestbuy-categories-curated.txt</Text> from the DataRetrieval tool.
        Parses <Text span ff="monospace" size="sm">## Category {'>'} Subcategory</Text> headers into a two-level hierarchy.
        Existing categories are skipped.
      </Alert>

      <Dropzone
        onDrop={(files) => { setFile(files[0]); setResult(null); setError(null); }}
        accept={['text/plain']}
        maxFiles={1}
        maxSize={5 * 1024 * 1024}
        loading={loading}
      >
        <Group justify="center" gap="xl" mih={80} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept><IconUpload size={32} color="var(--mantine-color-violet-6)" stroke={1.5} /></Dropzone.Accept>
          <Dropzone.Reject><IconX size={32} color="var(--mantine-color-red-6)" stroke={1.5} /></Dropzone.Reject>
          <Dropzone.Idle><IconFile size={32} color="var(--mantine-color-dimmed)" stroke={1.5} /></Dropzone.Idle>
          <Stack gap={4} align="center">
            <Text size="sm" fw={500}>
              {file ? file.name : 'Drop bestbuy-categories-curated.txt here'}
            </Text>
            <Text size="xs" c="dimmed">
              {file ? `${(file.size / 1024).toFixed(1)} KB — ready to import` : '.txt file up to 5 MB'}
            </Text>
          </Stack>
        </Group>
      </Dropzone>

      <Group justify="flex-end">
        {file && (
          <Button variant="subtle" color="gray" size="xs" onClick={() => { setFile(null); setResult(null); }}>
            Clear
          </Button>
        )}
        <Button
          leftSection={<IconUpload size={14} />}
          color="violet"
          loading={loading}
          disabled={!file}
          onClick={handleImport}
          size="sm"
        >
          {loading ? 'Importing...' : 'Import Categories'}
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">{error}</Alert>
      )}

      {result && (
        <Paper shadow="xs" radius="md" withBorder p="md">
          <Stack gap="sm">
            <Group gap="sm">
              <ThemeIcon color="green" variant="light" size="md">
                <IconCircleCheck size={16} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">Import Complete</Text>
                <Text size="xs" c="dimmed">{result.message}</Text>
              </div>
            </Group>
            <Divider />
            <Group grow>
              <Stack gap={4} align="center">
                <Badge size="lg" color="violet" variant="light">{result.parentsCreated}</Badge>
                <Text size="xs" c="dimmed">Parents created</Text>
              </Stack>
              <Stack gap={4} align="center">
                <Badge size="lg" color="blue" variant="light">{result.subcategoriesCreated}</Badge>
                <Text size="xs" c="dimmed">Subcategories created</Text>
              </Stack>
              <Stack gap={4} align="center">
                <Badge size="lg" color="gray" variant="light">{result.skipped}</Badge>
                <Text size="xs" c="dimmed">Already existed</Text>
              </Stack>
              <Stack gap={4} align="center">
                <Badge size="lg" color={result.errorCount > 0 ? 'red' : 'gray'} variant="light">{result.errorCount}</Badge>
                <Text size="xs" c="dimmed">Errors</Text>
              </Stack>
            </Group>
            {result.errors.length > 0 && (
              <>
                <Divider />
                <Text size="xs" fw={500} c="red">Errors:</Text>
                <List size="xs" spacing={2}>
                  {result.errors.map((e, i) => (
                    <List.Item key={i}><Text size="xs" c="dimmed" ff="monospace">{e}</Text></List.Item>
                  ))}
                </List>
              </>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ── Product Import ────────────────────────────────────────────────────────────

function ProductImportSection() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ProductImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clearExisting, setClearExisting] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await adminService.importProductsCsv(file, clearExisting);
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Import failed. Check the API logs.');
    } finally {
      setLoading(false);
    }
  };

  const total = result ? result.imported + result.skipped : 0;
  const importPct = total > 0 ? Math.round((result!.imported / total) * 100) : 0;

  return (
    <Stack gap="md">
      <Group gap="xs">
        <ThemeIcon variant="light" color="blue" size="md">
          <IconUpload size={16} />
        </ThemeIcon>
        <Text fw={600} size="lg">Import Products</Text>
      </Group>

      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Upload <Text span ff="monospace" size="sm">products.csv</Text> from the Best Buy DataRetrieval tool.
        Only rows with <Text span ff="monospace" size="sm">Source=BestBuy</Text> are imported.
        Duplicate barcodes and categories are matched by subcategory name first, then parent.
      </Alert>

      <Dropzone
        onDrop={(files) => { setFile(files[0]); setResult(null); setError(null); }}
        accept={['text/csv', 'application/vnd.ms-excel', 'text/plain']}
        maxFiles={1}
        maxSize={50 * 1024 * 1024}
        loading={loading}
      >
        <Group justify="center" gap="xl" mih={80} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept><IconUpload size={32} color="var(--mantine-color-blue-6)" stroke={1.5} /></Dropzone.Accept>
          <Dropzone.Reject><IconX size={32} color="var(--mantine-color-red-6)" stroke={1.5} /></Dropzone.Reject>
          <Dropzone.Idle><IconFile size={32} color="var(--mantine-color-dimmed)" stroke={1.5} /></Dropzone.Idle>
          <Stack gap={4} align="center">
            <Text size="sm" fw={500}>
              {file ? file.name : 'Drop products.csv here or click to browse'}
            </Text>
            <Text size="xs" c="dimmed">
              {file ? `${(file.size / 1024).toFixed(1)} KB — ready to import` : 'CSV file up to 50 MB'}
            </Text>
          </Stack>
        </Group>
      </Dropzone>

      <Checkbox
        label="Replace existing Best Buy products"
        description="Deletes all products with Source=BestBuy (and their reviews) before importing."
        color="red"
        checked={clearExisting}
        onChange={(e) => setClearExisting(e.currentTarget.checked)}
      />

      {clearExisting && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          All existing Best Buy products and any reviews on them will be permanently deleted before import.
        </Alert>
      )}

      <Group justify="flex-end">
        {file && (
          <Button variant="subtle" color="gray" size="xs" onClick={() => { setFile(null); setResult(null); }}>
            Clear
          </Button>
        )}
        <Button
          leftSection={<IconUpload size={14} />}
          loading={loading}
          disabled={!file}
          onClick={handleImport}
          size="sm"
          color={clearExisting ? 'red' : undefined}
        >
          {loading ? 'Importing...' : clearExisting ? 'Replace & Import' : 'Import Products'}
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">{error}</Alert>
      )}

      {result && (
        <Paper shadow="xs" radius="md" withBorder p="md">
          <Stack gap="sm">
            <Group gap="sm">
              <ThemeIcon color="green" variant="light" size="md">
                <IconCircleCheck size={16} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">Import Complete</Text>
                <Text size="xs" c="dimmed">{result.message}</Text>
              </div>
            </Group>
            <Divider />
            <Group grow>
              {result.deleted > 0 && (
                <Stack gap={4} align="center">
                  <Badge size="lg" color="red" variant="light">{result.deleted.toLocaleString()}</Badge>
                  <Text size="xs" c="dimmed">Deleted</Text>
                </Stack>
              )}
              <Stack gap={4} align="center">
                <Badge size="lg" color="green" variant="light">{result.imported.toLocaleString()}</Badge>
                <Text size="xs" c="dimmed">Imported</Text>
              </Stack>
              <Stack gap={4} align="center">
                <Badge size="lg" color="gray" variant="light">{result.skipped.toLocaleString()}</Badge>
                <Text size="xs" c="dimmed">Skipped</Text>
              </Stack>
              <Stack gap={4} align="center">
                <Badge size="lg" color={result.errorCount > 0 ? 'red' : 'gray'} variant="light">
                  {result.errorCount.toLocaleString()}
                </Badge>
                <Text size="xs" c="dimmed">Errors</Text>
              </Stack>
            </Group>
            {total > 0 && (
              <Stack gap={4}>
                <Progress value={importPct} color="green" size="sm" />
                <Text size="xs" c="dimmed" ta="right">{importPct}% imported</Text>
              </Stack>
            )}
            {result.errors.length > 0 && (
              <>
                <Divider />
                <Text size="xs" fw={500} c="red">First {result.errors.length} errors:</Text>
                <List size="xs" spacing={2}>
                  {result.errors.map((e, i) => (
                    <List.Item key={i}><Text size="xs" c="dimmed" ff="monospace">{e}</Text></List.Item>
                  ))}
                </List>
              </>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminImportProducts() {
  return (
    <Stack gap="lg">
      <Title order={2}>Best Buy Import</Title>
      <Text c="dimmed" size="sm">
        Import categories first, then products — the product importer uses category names to assign products.
      </Text>
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        <Paper shadow="xs" radius="md" withBorder p="lg">
          <CategoryImportSection />
        </Paper>
        <Paper shadow="xs" radius="md" withBorder p="lg">
          <ProductImportSection />
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
